import { useEffect, useRef, useState } from "react";
import { Search, UserCheck, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { KIND, STATUS } from "@/lib/format";

function History({ email }) {
  const [rows, setRows] = useState(null);
  useEffect(() => { api.get("/admin/clients/history", { params: { email } }).then((r) => setRows(r.data)).catch(() => setRows([])); }, [email]);
  if (rows === null) return <p className="px-4 pb-3 text-xs text-slate-500">Caricamento storico…</p>;
  return (
    <div className="border-t border-[#cfe3b5] px-4 py-3" data-testid="client-history">
      <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-[#3d6b16]">Ultimi interventi</p>
      {!rows.length ? <p className="text-xs text-slate-500" data-testid="client-history-empty">Nessun intervento precedente.</p> : (
        <ul className="space-y-1.5">{rows.map((h, i) => (
          <li key={h.id} className="flex flex-wrap items-baseline justify-between gap-x-3 text-sm" data-testid={`client-history-item-${i}`}>
            <span><b>{new Date(`${h.date}T12:00:00`).toLocaleDateString("it-IT")}</b> {h.start} · {KIND[h.kind] || h.kind}{h.equipment_label ? ` · ${h.equipment_label}` : ""}{h.brand_model ? ` (${h.brand_model})` : ""}
              <span className="text-slate-500"> · {h.technician_name || "nessun tecnico"}</span>
              {h.summary && <span className="block text-xs text-slate-500">{h.summary}</span>}</span>
            <span className={`rounded-full border px-2 py-0.5 text-[11px] ${STATUS[h.status]?.cls || ""}`}>{STATUS[h.status]?.label || h.status}</span>
          </li>))}</ul>
      )}
    </div>
  );
}

const FIELDS = ["first_name", "last_name", "email", "phone", "address", "city", "tax_code"];
const norm = (s) => (s || "").replace(/\D/g, "").slice(-9);

function useSearch(q) {
  const [items, setItems] = useState([]);
  const ctrl = useRef(null);
  useEffect(() => {
    if (q.trim().length < 2) { setItems([]); return undefined; }
    const t = setTimeout(() => {
      ctrl.current?.abort();
      ctrl.current = new AbortController();
      api.get("/admin/clients/search", { params: { q }, signal: ctrl.current.signal }).then((r) => setItems(r.data)).catch(() => {});
    }, 300);
    return () => clearTimeout(t);
  }, [q]);
  return items;
}

const Item = ({ c, onPick, testid }) => (
  <button type="button" onClick={() => onPick(c)} className="flex w-full items-start justify-between gap-3 px-3 py-2 text-left hover:bg-slate-50" data-testid={testid}>
    <span><span className="block text-sm font-semibold">{c.first_name} {c.last_name}</span>
      <span className="block text-xs text-slate-500">{c.phone} · {c.email}{c.city ? ` · ${c.city}` : ""}</span></span>
    <span className="shrink-0 text-xs text-slate-400">{c.jobs} richieste</span>
  </button>
);

export function ClientLookup({ form, setForm }) {
  const [q, setQ] = useState("");
  const [picked, setPicked] = useState(null);
  const results = useSearch(q);
  const auto = useSearch(picked ? "" : form.phone?.replace(/\D/g, "").length >= 6 ? form.phone : form.email?.includes("@") ? form.email : "");
  const match = auto.find((c) => (form.email && c.email === form.email.trim().toLowerCase()) || (norm(form.phone).length >= 6 && norm(c.phone) === norm(form.phone)));
  const pick = (c) => {
    setForm((p) => ({ ...p, ...Object.fromEntries(FIELDS.map((k) => [k, c[k] || ""])), ...(c.lat != null ? { lat: c.lat, lng: c.lng, geo_source: "cliente" } : {}) }));
    setPicked(c); setQ("");
  };
  return (
    <div className="mb-5 space-y-2" data-testid="client-lookup">
      {picked ? (
        <div className="rounded-xl border border-[#cfe3b5] bg-[#f6faf0] text-sm" data-testid="client-lookup-picked">
          <div className="flex items-center justify-between gap-3 px-4 py-3">
            <span className="flex items-center gap-2"><UserCheck className="h-4 w-4 text-[#61a229]" />Cliente esistente: <b>{picked.first_name} {picked.last_name}</b> · dati compilati, puoi modificarli</span>
            <Button size="icon" variant="ghost" onClick={() => setPicked(null)} data-testid="client-lookup-clear"><X className="h-4 w-4" /></Button>
          </div>
          <History email={picked.email} />
        </div>
      ) : (
        <div className="relative">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca un cliente già presente: nome, telefono o email" className="pl-9" data-testid="client-lookup-input" />
          {results.length > 0 && <div className="absolute z-20 mt-1 w-full divide-y overflow-hidden rounded-xl border bg-white shadow-lg" data-testid="client-lookup-results">
            {results.map((c, i) => <Item key={c.email} c={c} onPick={pick} testid={`client-lookup-item-${i}`} />)}</div>}
          {q.trim().length >= 2 && !results.length && <p className="mt-1 text-xs text-slate-500" data-testid="client-lookup-none">Nessun cliente trovato: compila i dati qui sotto per crearne uno nuovo.</p>}
        </div>
      )}
      {!picked && match && (
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900" data-testid="client-lookup-match">
          <span>{form.email && match.email === form.email.trim().toLowerCase()
            ? <>Questo contatto esiste già: <b>{match.first_name} {match.last_name}</b> ({match.phone}, {match.email})</>
            : <>Numero già usato da <b>{match.first_name} {match.last_name}</b> ({match.email}): puoi usare i suoi dati oppure continuare a creare un cliente diverso con lo stesso numero.</>}</span>
          <Button size="sm" className="rounded-full" onClick={() => pick(match)} data-testid="client-lookup-use-match">Usa i suoi dati</Button>
        </div>
      )}
    </div>
  );
}
